// src/components/charger/ChargerForm.js
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Paper,
  InputAdornment,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import HttpIcon from '@mui/icons-material/Http';
import PhoneIcon from '@mui/icons-material/Phone';
import MemoryIcon from '@mui/icons-material/Memory';

// Define validation schema
const schema = yup.object({
  ChargerName: yup.string().required('Charger name is required'),
  ChargerEnabled: yup.boolean(),
  ChargerBrand: yup.string().nullable(),
  ChargerModel: yup.string().nullable(),
  ChargerType: yup.string().nullable(),
  ChargerSerial: yup.string().nullable(),
  ChargerMeter: yup.string().nullable(),
  ChargerMeterSerial: yup.string().nullable(),
  ChargerPincode: yup.string().nullable(),
  ChargerWsURL: yup.string().nullable(),
  ChargerICCID: yup.string().nullable(),
  ChargerAvailability: yup.string().nullable(),
  ChargerIsOnline: yup.boolean(),
  ChargerAccessType: yup.string().nullable(),
  ChargerActive24x7: yup.boolean()
}).required();

const chargerTypes = [
  'AC',
  'DC',
  'AC/DC',
  'Level 1',
  'Level 2',
  'Level 3',
  'Fast Charger',
  'Slow Charger'
];

const accessTypes = [
  'Public',
  'Private',
  'Restricted',
  'Membership',
  'Pay-As-You-Go'
];

const ChargerForm = ({ charger, companyId, siteId, onSubmit, isLoading }) => {
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: charger ? {
      ChargerName: charger.ChargerName,
      ChargerEnabled: charger.ChargerEnabled,
      ChargerBrand: charger.ChargerBrand || '',
      ChargerModel: charger.ChargerModel || '',
      ChargerType: charger.ChargerType || '',
      ChargerSerial: charger.ChargerSerial || '',
      ChargerMeter: charger.ChargerMeter || '',
      ChargerMeterSerial: charger.ChargerMeterSerial || '',
      ChargerPincode: charger.ChargerPincode || '',
      ChargerWsURL: charger.ChargerWsURL || '',
      ChargerICCID: charger.ChargerICCID || '',
      ChargerAvailability: charger.ChargerAvailability || '',
      ChargerIsOnline: charger.ChargerIsOnline || false,
      ChargerAccessType: charger.ChargerAccessType || '',
      ChargerActive24x7: charger.ChargerActive24x7 || true,
    } : {
      ChargerName: '',
      ChargerEnabled: true,
      ChargerBrand: '',
      ChargerModel: '',
      ChargerType: '',
      ChargerSerial: '',
      ChargerMeter: '',
      ChargerMeterSerial: '',
      ChargerPincode: '',
      ChargerWsURL: '',
      ChargerICCID: '',
      ChargerAvailability: '',
      ChargerIsOnline: false,
      ChargerAccessType: '',
      ChargerActive24x7: true,
    }
  });

  const onFormSubmit = (data) => {
    // Add company ID and site ID to the form data for charger creation
    const formData = {
      ...data,
      ChargerCompanyId: companyId,
      ChargerSiteId: siteId,
      // Also add ChargerId for API compatibility
      ChargerId: charger?.ChargerId || Math.floor(Math.random() * 10000) // Generate a random ID if new charger
    };
    
    // Convert empty strings to null
    Object.keys(formData).forEach(key => {
      if (formData[key] === '') {
        formData[key] = null;
      }
    });
    
    onSubmit(formData);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3} direction="column">
          {/* Toggle Switches Row */}
          <Grid item xs={12}>
            <Box display="flex" gap={4} mb={2}>
              <Controller
                name="ChargerEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
              <Controller
                name="ChargerActive24x7"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Active 24/7"
                  />
                )}
              />
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="ChargerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Charger Name"
                  fullWidth
                  error={!!errors.ChargerName}
                  helperText={errors.ChargerName?.message}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EvStationIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="ChargerType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="charger-type-label" shrink>Charger Type</InputLabel>
                  <Select
                    {...field}
                    labelId="charger-type-label"
                    label="Charger Type"
                    error={!!errors.ChargerType}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {chargerTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="ChargerAccessType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="access-type-label" shrink>Access Type</InputLabel>
                  <Select
                    {...field}
                    labelId="access-type-label"
                    label="Access Type"
                    error={!!errors.ChargerAccessType}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {accessTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="ChargerAvailability"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Availability Schedule"
                  fullWidth
                  error={!!errors.ChargerAvailability}
                  helperText={errors.ChargerAvailability?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {charger ? 'Update' : 'Create'} Charger
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default ChargerForm;